/**
 * Contact.tsx
 * 
 * Design Philosophy: Editorial Minimalism with Architectural Precision
 * - Clean typography hierarchy with generous whitespace
 * - Muted earth tones (warm beige, soft grey, deep burgundy accents)
 * - Three contact options: Form (Formspree), WhatsApp, Calendly
 * 
 * TODO: Replace placeholders with real data:
 * - FORMSPREE_ENDPOINT: https://formspree.io/f/YOUR_FORM_ID
 * - CALENDLY_URL: https://calendly.com/YOUR_USERNAME
 */

import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Calendar, Mail, Phone, MapPin, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xreoorlo";
const CALENDLY_URL = "https://calendly.com/brasovgateway/30min";

const SERVICES = [
  "Real Estate",
  "Company Setup",
  "Relocation Support",
  "Business Opportunities",
  "Just exploring",
];

export default function Contact() {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    service: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(formState),
      });
      if (res.ok) {
        setSubmitted(true);
      }
    } catch {
      // Network error — show error state
      alert("Something went wrong. Please try again or contact us directly.");
    }
    setSubmitting(false);
  };

  return (
    <>
    <Helmet>
      <title>Contact Brașov Gateway — Get in Touch with Our Team</title>
      <meta name="description" content="Have questions about relocating to Brașov? Contact the Brașov Gateway team for advice on real estate, company setup, visas, and life in Romania." />
      <meta property="og:title" content="Contact Brașov Gateway — Get in Touch with Our Team" />
      <meta property="og:description" content="Have questions about relocating to Brașov? Contact the Brașov Gateway team for advice on real estate, company setup, visas, and life in Romania." />
      <meta property="og:image" content="https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/home-hero-brasov-v2_fd2b2f8b.webp" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://brasovgateway.com/contact" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Contact Brașov Gateway — Get in Touch with Our Team" />
      <link rel="canonical" href="https://brasovgateway.com/contact" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "@id": "https://brasovgateway.com/contact",
          "name": "Contact Brașov Gateway",
          "url": "https://brasovgateway.com/contact",
          "description": "Get in touch with Brașov Gateway for real estate advisory, company setup, and relocation support in Brașov, Romania.",
          "publisher": {
            "@type": "Organization",
            "name": "Brașov Gateway",
            "url": "https://brasovgateway.com"
          }
        }
      `}</script>
    </Helmet>
    <div className="min-h-screen bg-background" style={{ fontFamily: "'Source Sans 3', sans-serif" }}>
      <SiteHeader />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4" style={{ backgroundColor: "oklch(0.985 0.006 75)" }}>
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.2em] mb-4" style={{ color: "oklch(0.38 0.12 15)" }}>
            Get in touch
          </p>
          <h1 className="text-5xl md:text-6xl mb-6 text-stone-900 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Let's talk about<br />
            <em className="not-italic" style={{ color: "oklch(0.55 0.1 40)" }}>what you need</em>
          </h1>
          <p className="text-lg text-stone-500 max-w-2xl leading-relaxed">
            No commitment, no sales pitch. Just an honest conversation about what you're trying to do — and whether I'm the right person to help.
          </p>
        </div>
      </section>

      {/* Three contact options */}
      <section className="py-16 px-4 bg-white">
        <div className="container max-w-4xl">
          <div className="grid md:grid-cols-2 gap-6 mb-16">

            {/* Calendly */}
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="no-underline group"
            >
              <div className="border border-stone-200 p-6 hover:border-stone-400 transition-all hover:shadow-sm h-full flex flex-col">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: "oklch(0.92 0.06 240)" }}>
                  <Calendar size={20} style={{ color: "oklch(0.38 0.12 240)" }} />
                </div>
                <h3 className="text-lg font-semibold text-stone-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Book a call
                </h3>
                <p className="text-sm text-stone-500 leading-relaxed flex-1">
                  30-minute consultation. Pick a time that works for you — no back-and-forth emails.
                </p>
                <div className="mt-4 flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all" style={{ color: "oklch(0.38 0.12 240)" }}>
                  <span>See availability</span>
                  <span>→</span>
                </div>
              </div>
            </a>

            {/* Email / Form */}
            <div className="border-2 p-6 h-full flex flex-col" style={{ borderColor: "oklch(0.38 0.12 15)" }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: "oklch(0.95 0.02 20)" }}>
                <Mail size={20} style={{ color: "oklch(0.38 0.12 15)" }} />
              </div>
              <h3 className="text-lg font-semibold text-stone-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Send a message
              </h3>
              <p className="text-sm text-stone-500 leading-relaxed flex-1">
                Describe your situation in detail. I read every message personally and reply within 24 hours.
              </p>
              <div className="mt-4 flex items-center gap-2 text-sm font-medium" style={{ color: "oklch(0.38 0.12 15)" }}>
                <span>Scroll down ↓</span>
              </div>
            </div>
          </div>

          {/* Info strip */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-8 border-t border-b border-stone-100">
            <div className="flex items-start gap-3">
              <MapPin size={16} className="text-stone-400 mt-1 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-widest text-stone-400 mb-1">Location</p>
                <p className="text-sm text-stone-700">Brașov, Romania</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone size={16} className="text-stone-400 mt-1 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-widest text-stone-400 mb-1">Response time</p>
                <p className="text-sm text-stone-700">Within 24 hours</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail size={16} className="text-stone-400 mt-1 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-widest text-stone-400 mb-1">Languages</p>
                <p className="text-sm text-stone-700">Romanian & English</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 px-4" style={{ backgroundColor: "oklch(0.985 0.006 75)" }}>
        <div className="container max-w-2xl">
          <h2 className="text-3xl md:text-4xl mb-3 text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Send a message
          </h2>
          <p className="text-stone-500 mb-10">
            Tell me about your plans. The more context you give, the more useful my reply will be.
          </p>

          {submitted ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: "oklch(0.92 0.08 145)" }}>
                <CheckCircle size={32} style={{ color: "oklch(0.38 0.12 145)" }} />
              </div>
              <h3 className="text-2xl font-semibold text-stone-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Message sent
              </h3>
              <p className="text-stone-500 max-w-sm">
                Thank you for reaching out. I'll read your message and get back to you within 24 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 mb-2">Your name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formState.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className="w-full bg-white border border-stone-200 text-stone-900 text-sm px-4 py-3 outline-none focus:border-stone-400 transition-colors placeholder:text-stone-300"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 mb-2">Email address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formState.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className="w-full bg-white border border-stone-200 text-stone-900 text-sm px-4 py-3 outline-none focus:border-stone-400 transition-colors placeholder:text-stone-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-500 mb-2">I'm interested in</label>
                <select
                  name="service"
                  value={formState.service}
                  onChange={handleChange}
                  className="w-full bg-white border border-stone-200 text-stone-900 text-sm px-4 py-3 outline-none focus:border-stone-400 transition-colors appearance-none"
                >
                  <option value="">Select a service (optional)</option>
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-500 mb-2">Your message *</label>
                <textarea
                  name="message"
                  required
                  rows={6}
                  value={formState.message}
                  onChange={handleChange}
                  placeholder="Tell me about your plans — what you're looking for, your timeline, budget range, or any questions you have..."
                  className="w-full bg-white border border-stone-200 text-stone-900 text-sm px-4 py-3 outline-none focus:border-stone-400 transition-colors placeholder:text-stone-300 resize-none"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                <p className="text-xs text-stone-400">
                  I reply within 24 hours. No spam, ever.
                </p>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 px-8 py-3 text-white text-sm uppercase tracking-widest"
                  style={{ backgroundColor: "oklch(0.38 0.12 15)", fontFamily: "'Source Sans 3', sans-serif" }}
                >
                  {submitting ? "Sending..." : (
                    <>
                      Send message
                      <Send size={14} />
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
    </>
  );
}
